/**
 * Utility functions for dynamic branding based on hostname
 */

/**
 * Get company name based on the current hostname
 * @returns {string} Company name
 */
export const getCompanyName = () => {
  if (typeof window === 'undefined') {
    return 'JBNworks'; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name and convert to company name
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, extract the subdomain
    domainName = hostname.split('.')[0];
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  // Convert domain name to proper company name
  const companyNameMap = {
    'jbnworks': 'JBNworks',
    'creativecloud360': 'Creative Cloud 360',
    'creativeclouds360': 'Creative Cloud 360'
  };
  
  return companyNameMap[domainName] || domainName.charAt(0).toUpperCase() + domainName.slice(1);
};

/**
 * Get company email based on the current hostname
 * @returns {string} Company email
 */
export const getCompanyEmail = () => {
  if (typeof window === 'undefined') {
    return 'info@jbnworks.com'; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name from hostname
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, extract the subdomain (e.g., 'jbnworks' from 'jbnworks.azurewebsites.net')
    domainName = hostname.split('.')[0];
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  return `info@${domainName}.com`;
};

/**
 * Get contact email based on the current hostname
 * @returns {string} Contact email
 */
export const getContactEmail = () => {
  if (typeof window === 'undefined') {
    return 'info@jbnworks.com'; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name from hostname
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, extract the subdomain (e.g., 'jbnworks' from 'jbnworks.azurewebsites.net')
    domainName = hostname.split('.')[0];
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  return `contact@${domainName}.com`;
};

/**
 * Get info email based on the current hostname
 * @returns {string} Info email
 */
export const getInfoEmail = () => {
  if (typeof window === 'undefined') {
    return 'info@jbnworks.com'; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name from hostname
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, extract the subdomain (e.g., 'jbnworks' from 'jbnworks.azurewebsites.net')
    domainName = hostname.split('.')[0];
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  return `info@${domainName}.com`;
};

/**
 * Get company domain based on the current hostname
 * @returns {string} Company domain
 */
export const getCompanyDomain = () => {
  if (typeof window === 'undefined') {
    return 'jbnworks.com'; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name from hostname
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, return the actual Azure domain
    return hostname;
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  return `${domainName}.com`;
};

/**
 * Get company logo path based on the current hostname and theme
 * @param {boolean} isDark - Whether to use dark theme logo
 * @param {boolean} isNavbar - Whether this is for navbar (smaller version)
 * @returns {string} Logo path
 */
export const getCompanyLogo = (isDark = false, isNavbar = false) => {
  if (typeof window === 'undefined') {
    const suffix = isNavbar ? '-navbar' : '';
    const theme = isDark ? '-white' : '';
    return `/images/logos/jbnworks-logo${suffix}${theme}.svg`; // Default for server-side rendering
  }
  
  const hostname = window.location.hostname;
  
  // Extract domain name from hostname
  let domainName;
  if (hostname.includes('azurewebsites.net')) {
    // For Azure websites, extract the subdomain
    domainName = hostname.split('.')[0];
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    domainName = 'jbnworks';
  } else {
    // For regular domains, remove 'www.' if present
    domainName = hostname.replace('www.', '').split('.')[0];
  }
  
  const navbarSuffix = isNavbar ? '-navbar' : '';
  const themeSuffix = isDark ? '-white' : '';
  return `/images/logos/${domainName}-logo${navbarSuffix}${themeSuffix}.svg`;
};
